/**
 * Xena Workwear — UTM Persistence & Attribution Script
 * Purpose: Captures UTM parameters and ad click IDs on landing,
 * stores them in a first-party cookie, and injects them as Shopify
 * cart attributes so every order carries its traffic source.
 *
 * This fixes the "Direct" / "Unknown" attribution problem where
 * UTM params are lost when users navigate away from the landing page.
 *
 * Added: April 2026
 */
(function () {
  'use strict';

  var COOKIE_NAME = '_xena_utm';
  var COOKIE_DAYS = 30;
  var UTM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_id'
  ];
  var CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid', 'li_fat_id', 'rdt_cid'];
  var ALL_KEYS = UTM_KEYS.concat(CLICK_ID_KEYS);

  /* ── Cookie helpers ───────────────────────────────── */

  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 86400000);
    document.cookie =
      name +
      '=' +
      encodeURIComponent(value) +
      ';expires=' +
      d.toUTCString() +
      ';path=/;SameSite=Lax;Secure';
  }

  function getCookie(name) {
    var match = document.cookie.match(
      new RegExp('(?:^|;\\s*)' + name + '=([^;]*)')
    );
    return match ? decodeURIComponent(match[1]) : null;
  }

  /* ── Capture UTMs from current URL ────────────────── */

  function captureFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var data = {};
    var hasAny = false;

    ALL_KEYS.forEach(function (key) {
      var val = params.get(key);
      if (val) {
        data[key] = val;
        hasAny = true;
      }
    });

    // Also capture the landing page and referrer on first touch
    if (hasAny) {
      data._landing = window.location.pathname;
      data._referrer = document.referrer || '(none)';
      data._ts = new Date().toISOString();
    }

    return hasAny ? data : null;
  }

  /* ── Capture referrer for organic / social fallback ── */

  function captureReferrer() {
    var ref = document.referrer;
    if (!ref) return null;

    // Don't overwrite with self-referral
    try {
      var refHost = new URL(ref).hostname;
      if (refHost === window.location.hostname) return null;
    } catch (e) {
      return null;
    }

    // Map known referrer domains to source/medium
    var referrerMap = {
      'google.com': { s: 'google', m: 'organic' },
      'google.co': { s: 'google', m: 'organic' },
      'bing.com': { s: 'bing', m: 'organic' },
      'yahoo.com': { s: 'yahoo', m: 'organic' },
      'duckduckgo.com': { s: 'duckduckgo', m: 'organic' },
      'facebook.com': { s: 'facebook', m: 'social' },
      'm.facebook.com': { s: 'facebook', m: 'social' },
      'l.facebook.com': { s: 'facebook', m: 'social' },
      'lm.facebook.com': { s: 'facebook', m: 'social' },
      'instagram.com': { s: 'instagram', m: 'social' },
      'l.instagram.com': { s: 'instagram', m: 'social' },
      'tiktok.com': { s: 'tiktok', m: 'social' },
      'pinterest.com': { s: 'pinterest', m: 'social' },
      'linkedin.com': { s: 'linkedin', m: 'social' },
      'twitter.com': { s: 'twitter', m: 'social' },
      'x.com': { s: 'twitter', m: 'social' },
      'reddit.com': { s: 'reddit', m: 'social' },
      'youtube.com': { s: 'youtube', m: 'social' }
    };

    var matched = null;
    Object.keys(referrerMap).forEach(function (domain) {
      if (refHost.indexOf(domain) !== -1) {
        matched = referrerMap[domain];
      }
    });

    if (matched) {
      return {
        utm_source: matched.s,
        utm_medium: matched.m,
        _landing: window.location.pathname,
        _referrer: ref,
        _ts: new Date().toISOString(),
        _inferred: 'true'
      };
    }

    // Generic referral
    return {
      utm_source: refHost,
      utm_medium: 'referral',
      _landing: window.location.pathname,
      _referrer: ref,
      _ts: new Date().toISOString(),
      _inferred: 'true'
    };
  }

  /* ── Main logic ───────────────────────────────────── */

  // 1. Try to capture UTMs from URL (highest priority — explicit params)
  var utmData = captureFromUrl();

  if (utmData) {
    // New UTM params found — always overwrite cookie (last-click wins)
    setCookie(COOKIE_NAME, JSON.stringify(utmData), COOKIE_DAYS);
  }

  // 2. If no UTMs in URL and no existing cookie, try referrer fallback
  if (!utmData && !getCookie(COOKIE_NAME)) {
    var refData = captureReferrer();
    if (refData) {
      setCookie(COOKIE_NAME, JSON.stringify(refData), COOKIE_DAYS);
    }
  }

  // 3. Read stored UTM data (may be from this page or a previous visit)
  var stored = null;
  var raw = getCookie(COOKIE_NAME);
  if (raw) {
    try {
      stored = JSON.parse(raw);
    } catch (e) {
      stored = null;
    }
  }

  if (!stored) return;

  /* ── Inject UTMs into Shopify cart as note attributes ── */

  function injectCartAttributes() {
    // Build the attribute payload from stored UTMs
    var attrs = {};
    var attrKeys = UTM_KEYS.concat(CLICK_ID_KEYS).concat(['_landing', '_referrer']);
    attrKeys.forEach(function (key) {
      if (stored[key]) {
        attrs[key] = stored[key];
      }
    });

    // Use Shopify's Cart API to set note attributes
    // This persists UTM data through to checkout and order
    var payload = { attributes: attrs };

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).catch(function () {
      // Silent fail — don't break the shopping experience
    });
  }

  // Inject on DOMContentLoaded to avoid blocking page render
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', injectCartAttributes);
  } else {
    injectCartAttributes();
  }

  /* ── Expose for debugging (accessible via console) ── */
  window.__xenaUtm = {
    stored: stored,
    cookie: COOKIE_NAME,
    version: '1.0.0'
  };
})();